import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { bootLogs } from "../data/mock";

const banner = [
  "  .   ____          _            __ _ _",
  " /\\\\ / ___'_ __ _ _(_)_ __  __ _ \\ \\ \\ \\",
  "( ( )\\___ | '_ | '_| | '_ \\/ _` | \\ \\ \\ \\",
  " \\\\/  ___)| |_)| | | | | || (_| |  ) ) ) )",
  "  '  |____| .__|_| |_|_| |_\\__, | / / / /",
  " =========|_|==============|___/=/_/_/_/",
  " :: VedantOS ::          (v1.0.0 · portfolio-runtime)"
];

const typeColor = {
  info: "text-cyan-200/90",
  success: "text-emerald-300",
  ready: "text-amber-300"
};

const LoadingScreen = ({ onDone }) => {
  const [visible, setVisible] = useState([]);
  const [show, setShow] = useState(true);

  useEffect(() => {
    const timers = bootLogs.map((log, i) =>
      setTimeout(() => {
        setVisible((v) => [...v, { ...log, idx: i }]);
      }, log.delay)
    );
    const hide = setTimeout(() => {
      setShow(false);
      if (onDone) setTimeout(onDone, 500);
    }, 2200);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(hide);
    };
  }, [onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          exit={{ opacity: 0, scale: 1.05, filter: "blur(20px)" }}
          transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
          className="fixed inset-0 z-[100] bg-[#05060a] flex items-center justify-center overflow-hidden"
        >
          {/* Scanlines overlay */}
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, rgba(34,211,238,0.4) 0px, rgba(34,211,238,0.4) 1px, transparent 1px, transparent 3px)"
            }}
          />
          <div className="absolute inset-0 bg-grid opacity-30" />
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-cyan-500/10 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-amber-500/10 blur-3xl" />

          <div className="relative w-[min(720px,92vw)] h-[min(520px,80vh)] rounded-2xl glass-strong shadow-2xl overflow-hidden">
            {/* Terminal chrome */}
            <div className="flex items-center gap-2 h-9 px-4 border-b border-white/5 bg-[#0a0b10]">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
              <span className="flex-1 text-center font-mono text-[11px] text-zinc-500 tracking-wider">
                vedant@portfolio — zsh — 120×30
              </span>
              <span className="font-mono text-[10px] text-emerald-300/70">● live</span>
            </div>

            {/* Terminal body */}
            <div className="p-5 font-mono text-[12.5px] leading-relaxed h-[calc(100%-2.25rem)] overflow-hidden">
              {/* Banner */}
              <motion.pre
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="text-cyan-300/90 text-[10px] leading-[1.1] mb-3 whitespace-pre"
              >
                {banner.join("\n")}
              </motion.pre>

              {/* Logs */}
              <div className="space-y-1.5">
                {visible
                  .filter((l) => l.type !== "banner")
                  .map((log) => (
                    <motion.div
                      key={log.idx}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.25 }}
                      className="flex gap-2"
                    >
                      <span className="text-zinc-600 shrink-0">
                        [{String(log.idx).padStart(2, "0")}]
                      </span>
                      <span
                        className={`shrink-0 ${
                          log.type === "ready" ? "text-amber-300" : "text-cyan-300/80"
                        }`}
                      >
                        {log.type === "ready" ? "➜" : "✓"}
                      </span>
                      <span className={typeColor[log.type] || "text-zinc-300"}>
                        {log.text}
                      </span>
                    </motion.div>
                  ))}
                {/* Blinking cursor */}
                <motion.span
                  animate={{ opacity: [1, 0, 1] }}
                  transition={{ duration: 0.9, repeat: Infinity }}
                  className="inline-block w-2 h-4 bg-cyan-300 align-middle ml-1"
                />
              </div>
            </div>
          </div>

          {/* Small label */}
          <div className="absolute bottom-8 left-0 right-0 text-center">
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-600">
              Booting portfolio runtime — please wait
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
